import { useMutation, useQueryClient } from "@tanstack/react-query";

import { invalidateSkillCollectionVersionQueries } from "./cache-utils";

import { ENDPOINTS } from "@/api/application-api";
import { useAuth } from "@/context/auth-context";
import { ToastType } from "@/enums";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { jsonAuthHeaders } from "@/utils/headers";
import { displayErrorToast, displayToast } from "@/utils/toast";

export interface ArchiveSkillCollectionVersionRequest {
  workspaceId: number;
  collectionId: string;
  skill_collection_version_id: string;
  restore?: boolean;
}

export interface ArchiveSkillCollectionVersionResponse {
  success: boolean;
  message: string;
}

export const useArchiveSkillCollectionVersion = () => {
  const queryClient = useQueryClient();
  const auth = useAuth();
  const userToken = auth?.userToken ?? "";

  return useMutation<
    ArchiveSkillCollectionVersionResponse,
    Error,
    ArchiveSkillCollectionVersionRequest
  >({
    mutationFn: async ({ workspaceId, collectionId, skill_collection_version_id, restore }) => {
      const res = await fetch(
        ENDPOINTS.skill_collections.version_archive(
          workspaceId,
          collectionId,
          skill_collection_version_id,
        ),
        {
          method: "POST",
          headers: jsonAuthHeaders(userToken),
          body: JSON.stringify({ restore: restore ?? false }),
        },
      );
      return handleResponse(res);
    },
    onSuccess: async (_data, variables) => {
      displayToast(
        `Version ${variables.restore ? "restored" : "hidden"} successfully`,
        ToastType.success,
      );

      await invalidateSkillCollectionVersionQueries(queryClient, variables);
    },
    onError: (error, variables) => {
      let message = "Unknown error";
      if (error instanceof Error) {
        const { message: errMessage } = error;
        message = errMessage;
      } else if (typeof error === "string") {
        message = error;
      }
      displayErrorToast(
        message || `Failed to ${variables.restore ? "restore" : "hide"} skill collection version`,
      );
    },
  });
};
